// Envío de mensajes — modelo simple alineado con la landing (2026-05-04 tarde).
//
// Una clave X25519 por cuenta. Sin Forward Secrecy. Sin per-device. Sin X3DH.
// Cifrado: sealed_box(plaintext, peerPub) con XSalsa20-Poly1305.
// Sender envelope: sealed_box(senderId, peerPub) — sealed sender.
// Una sola copia por receptor humano (no fan-out per-device).
//
// Outbox local persistente para reintentos offline. Multi-device es trivial: cualquier
// device del usuario, con login + password, descifra todo (priv común derivada del password).

import * as crypto from '../crypto';
import { api, type IncomingBlob } from '../api';
import * as local from './storage_local';
import { ulid } from '../ulid';
import * as archive from './conv_archive';
import { pseudonymOf } from '../identity_freq';
import type { OutboxEntry } from './storage_local';

export interface SendContext {
  myPriv: Uint8Array;             // priv X25519 de la cuenta efectiva (real o pseudo)
  myPub: Uint8Array;              // pub correspondiente
  mySyphraxId: string;            // syphrax_id efectivo (real o pseudonym de freq)
  realSyphraxId: string;          // syphrax_id real (para archive write-through y self-detection)
  pseudoFreqLabel: string;        // '__general__' o nombre de la freq
  pseudoFreqSecret: string | null;
  archiveMasterKey: Uint8Array;   // wrappingKey de la cuenta efectiva (Argon2id del password)
}

export interface SendResult {
  outboxId: string;
  serverId: string;
  status: 'sent' | 'pending' | 'failed';
}

function randomId(): string {
  const arr = new Uint8Array(16);
  globalThis.crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Cache de pubkey por syphraxId. TTL corto para no machacar /users/:id.
const pubKeyCache = new Map<string, { fetchedAt: number; pub: Uint8Array }>();
const PUBKEY_TTL_MS = 30 * 1000;

export async function getPeerPub(syphraxId: string, force = false): Promise<Uint8Array> {
  const now = Date.now();
  const cached = pubKeyCache.get(syphraxId);
  if (!force && cached && now - cached.fetchedAt < PUBKEY_TTL_MS) return cached.pub;
  const r = await api.getUser(syphraxId);
  const pub = crypto.b64decode(r.public_key);
  pubKeyCache.set(syphraxId, { fetchedAt: now, pub });
  return pub;
}

export async function enqueue(ctx: SendContext, p: {
  receiverSyphraxId: string;
  plaintext: string;
  type: 'msg' | 'media' | 'ctrl';
}): Promise<OutboxEntry> {
  void ctx;
  const messageId = ulid();
  const entry: OutboxEntry = {
    messageId,
    id: randomId(),
    freqLabel: ctx.pseudoFreqLabel,
    receiverSyphraxId: p.receiverSyphraxId,
    receiverDeviceIds: [],   // legacy field, ignored
    selfDeviceIds: [],       // legacy field, ignored
    type: p.type,
    plaintext: p.plaintext,
    createdAt: new Date().toISOString(),
    status: 'pending',
    retries: 0,
    nextRetryAt: new Date().toISOString(),
  };
  await local.outboxPut(entry);
  return entry;
}

async function sendOneCopy(
  ctx: SendContext,
  entry: OutboxEntry,
): Promise<{ blobId: string }> {
  // Si estamos en una freq, el peer aparece ante el server bajo su PSEUDO en
  // esa freq (= pseudonymOf(realPeerId, freqSecret)). Para que el blob llegue
  // a la pseudo-cuenta del peer (a la que está suscrita su sesión), enrutamos
  // por el pseudo. archive_chunk se sigue indexando por el realId humano para
  // que sender y receiver compartan conversation_tag.
  const routedReceiverId = ctx.pseudoFreqSecret
    ? pseudonymOf(entry.receiverSyphraxId, ctx.pseudoFreqSecret)
    : entry.receiverSyphraxId;
  const peerPub = await getPeerPub(routedReceiverId);

  // sealed_box del plaintext y del sender envelope. crypto_box_seal genera ephemeral
  // keypair internamente y nunca reusa nonce — no hay state que mantener.
  const ciphertext = crypto.sealedBoxEncrypt(entry.plaintext, peerPub);
  // Envelope contiene el REAL id del sender (no el pseudo). El receptor obtiene
  // así el id humano del peer y archive_chunk se indexa coherentemente con el
  // del sender. El server no ve el envelope (sealed_box).
  const senderEnvelope = crypto.sealedBoxEncrypt(ctx.realSyphraxId, peerPub);

  const receiverTag = crypto.generalTag(routedReceiverId);
  const senderTag = crypto.randomTag();

  const res = await api.sendMessage({
    receiver_tag: crypto.b64encode(receiverTag),
    sender_tag: crypto.b64encode(senderTag),
    freq_tag: null,
    nonce: crypto.b64encode(new Uint8Array(0)),
    ciphertext: crypto.b64encode(ciphertext),
    sender_envelope: crypto.b64encode(senderEnvelope),
    type: entry.type,
    message_id: entry.messageId,
  });
  return { blobId: res.id };
}

export async function flushOne(ctx: SendContext, entry: OutboxEntry): Promise<SendResult> {
  let serverId = '';
  try {
    const r = await sendOneCopy(ctx, entry);
    serverId = r.blobId;
  } catch (err) {
    entry.retries++;
    entry.error = (err as Error).message;
    entry.nextRetryAt = new Date(Date.now() + Math.min(60_000, 1000 * 2 ** entry.retries)).toISOString();
    await local.outboxPut(entry);
    return { outboxId: entry.id, serverId: '', status: 'pending' };
  }

  // Write-through al archive del sender. Idempotente: ON CONFLICT en server.
  if (entry.type === 'msg' || entry.type === 'media') {
    try {
      await archive.writeArchiveChunk(
        ctx.archiveMasterKey, ctx.realSyphraxId, entry.receiverSyphraxId, entry.freqLabel,
        { messageId: entry.messageId,
          conversationKey: { peerSyphraxId: entry.receiverSyphraxId, freqLabel: entry.freqLabel },
          fromMe: true, fromDeviceId: null,
          kind: entry.type, body: entry.plaintext,
          createdAt: entry.createdAt, op: 'append' },
      );
    } catch (_e) { /* archive failure no bloquea send */ }
  }
  entry.status = 'sent';
  entry.serverIds = { single: serverId };
  await local.outboxDelete(entry.id);
  return { outboxId: entry.id, serverId, status: 'sent' };
}

let flushInProgress = false;

export async function flushOutbox(ctx: SendContext): Promise<void> {
  if (flushInProgress) return;
  flushInProgress = true;
  try {
    const pending = await local.outboxListPending();
    const now = Date.now();
    for (const e of pending) {
      const due = new Date(e.nextRetryAt).getTime() <= now;
      if (!due) continue;
      try { await flushOne(ctx, e); } catch (_err) { /* siguiente */ }
    }
  } finally {
    flushInProgress = false;
  }
}

let flushTimer: number | null = null;

export function startBackgroundFlush(ctxFn: () => SendContext | null, intervalMs = 5000): () => void {
  if (flushTimer !== null) return () => stopBackgroundFlush();
  const tick = async () => {
    const ctx = ctxFn();
    if (ctx) {
      try { await flushOutbox(ctx); } catch (_e) { /* ignore */ }
    }
    flushTimer = window.setTimeout(tick, intervalMs);
  };
  flushTimer = window.setTimeout(tick, intervalMs);
  return () => stopBackgroundFlush();
}

export function stopBackgroundFlush(): void {
  if (flushTimer !== null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
}

export function triggerImmediateFlush(ctx: SendContext): Promise<void> {
  return flushOutbox(ctx);
}

// Envío directo one-shot (no entra en outbox). Mantenido por compat con call sites
// que necesitan saber si el blob salió YA. En el modelo nuevo es equivalente a un
// enqueue + flushOne sin persistencia.
export async function sendDirect(
  ctx: SendContext,
  targetSyphraxId: string,
  plaintext: string,
  type: 'msg' | 'media' | 'ctrl',
): Promise<{ blobId: string; messageId: string }> {
  const fakeEntry: OutboxEntry = {
    id: 'direct-' + Date.now() + '-' + Math.random().toString(36).slice(2),
    messageId: ulid(),
    freqLabel: ctx.pseudoFreqLabel,
    receiverSyphraxId: targetSyphraxId,
    receiverDeviceIds: [],
    selfDeviceIds: [],
    type,
    plaintext,
    createdAt: new Date().toISOString(),
    status: 'pending',
    retries: 0,
    nextRetryAt: new Date().toISOString(),
  };
  const r = await sendOneCopy(ctx, fakeEntry);
  return { blobId: r.blobId, messageId: fakeEntry.messageId };
}

// Compat shim: el código viejo llamaba a sendDirectToDevice(ctx, syphraxId, deviceId, ...)
// para history-sync entre devices propios. En el modelo nuevo no hay devices ni
// history-sync (cualquier device descarga del archive), así que este shim es no-op
// que devuelve un id falso para no romper el caller. El history-sync se eliminará
// cuando se limpie Chat.tsx.
export async function sendDirectToDevice(
  _ctx: SendContext,
  _targetSyphraxId: string,
  _targetDeviceId: string,
  _plaintext: string,
  _type: 'msg' | 'media' | 'ctrl',
  _targetIsSelf: boolean,
): Promise<{ blobId: string }> {
  return { blobId: 'noop' };
}

export type { IncomingBlob };
